/**
 * Utility file providing higher-order functions for managing asynchronous control flow.
 * These utilities help with common patterns like debouncing, throttling, and
 * preventing parallel execution of functions.
 */

/**
 * Type guard to check if a value is a function.
 * Used internally by other utilities to handle dynamic parameters.
 *
 * @param fn - The value to check
 * @returns True if the value is a function, false otherwise
 */
function isFunction(fn: unknown): fn is (...args: any[]) => any {
  return typeof fn === 'function';
}

/**
 * Creates a debounced version of a function that delays execution until a specified
 * wait time has passed since the last invocation.
 *
 * Key features:
 * - Supports dynamic wait times via function parameter
 * - Preserves the original function's `this` context
 * - Uses TypeScript's generic types to maintain parameter typing
 * - Resets the timer on each call, ensuring function only runs after quiet period
 *
 * Common use cases:
 * - Handling frequent events (scroll, resize, input)
 * - Delaying expensive operations until user stops interacting
 * - Preventing API call spam
 *
 * Example:
 * ```typescript
 * // Basic usage with search input
 * const searchAPI = (term: string) => fetch(`/api/search?q=${term}`);
 * const debouncedSearch = debounce(searchAPI, 300);
 *
 * // In an event handler
 * inputElement.addEventListener('input', (e) => {
 *   debouncedSearch(e.target.value); // Only calls API after typing stops for 300ms
 * });
 *
 * // With dynamic wait time
 * const adaptiveDebounce = debounce(saveFunction, () => {
 *   return navigator.onLine ? 500 : 3000; // Wait longer when offline
 * });
 * ```
 *
 * @param func - The function to debounce
 * @param wait - Milliseconds to wait, or a function returning milliseconds
 * @returns A debounced version of the original function
 */
export function debounce<T extends (...args: any[]) => void>(
  func: T,
  wait: number | (() => number)
): (...args: Parameters<T>) => void {
  // Timer reference to track and clear timeouts
  let timeout: ReturnType<typeof setTimeout> | undefined;

  // Helper to resolve wait time, handling both static and dynamic values
  const getWait = (): number => (isFunction(wait) ? (wait as () => number)() : (wait as number));

  return function debounced(this: ThisParameterType<T>, ...args: Parameters<T>) {
    // The function to execute after the wait period
    const later = () => {
      timeout = undefined;
      func.apply(this, args); // Execute with original context and arguments
    };

    // Reset timer on each call
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(later, getWait());
  };
}

/**
 * Executes a function immediately and then prevents further executions
 * until a quiet period has elapsed.
 *
 * Unlike standard debounce (which delays execution), this:
 * 1. Executes immediately on first call
 * 2. Blocks additional calls
 * 3. Resets after the specified quiet period
 *
 * Useful for:
 * - Immediate UI feedback with rate limiting
 * - "Save" buttons that should respond immediately but prevent double-clicks
 * - Events that should trigger once but not repeatedly in quick succession
 *
 * Example:
 * ```typescript
 * // Save button that prevents double-clicks
 * const saveData = (formData: FormData) => {
 *   return fetch('/api/save', { method: 'POST', body: formData });
 * };
 *
 * const saveOnce = runThenDebounce(saveData, 2000);
 *
 * saveButton.addEventListener('click', () => {
 *   const formData = new FormData(formElement);
 *   saveOnce(formData); // First click works immediately, subsequent clicks within 2s are ignored
 *
 *   // User feedback can still happen on every click
 *   saveButton.classList.add('clicked');
 *   setTimeout(() => saveButton.classList.remove('clicked'), 200);
 * });
 * ```
 *
 * @param func - The function to execute and then debounce
 * @param wait - Milliseconds to wait before allowing another execution
 * @returns A function that executes immediately but then debounces
 */
export function runThenDebounce<T extends (...args: any[]) => void>(
  func: T,
  wait: number | (() => number)
): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout> | undefined;
  let blocked = false; // Tracks whether calls are currently blocked

  const getWait = (): number => (isFunction(wait) ? (wait as () => number)() : (wait as number));

  return function runner(this: ThisParameterType<T>, ...args: Parameters<T>) {
    const later = () => {
      timeout = undefined;
      // Quiet period elapsed; allow the next call to fire immediately.
      blocked = false;
    };

    // Execute function if not in blocked state
    if (!blocked) {
      func.apply(this, args);
      blocked = true; // Block subsequent calls
    }

    // Reset the quiet period timer on each call
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(later, getWait());
  };
}

/**
 * Ensures a function can only have one execution active at a time.
 * Subsequent calls during execution are ignored.
 *
 * Key features:
 * - Works with both synchronous and asynchronous functions
 * - For async functions, waits for Promise to settle before unblocking
 * - Returns the original function's return value for successful calls
 * - Returns undefined for blocked calls
 * - Preserves exception behavior, ensuring error flow works normally
 *
 * Useful for:
 * - Preventing race conditions in state updates
 * - Guarding resource-intensive operations
 * - Click handlers that trigger expensive operations
 *
 * Example:
 * ```typescript
 * // Synchronous example - prevent multiple clicks
 * const processClick = (id: string) => {
 *   // Expensive operation like DOM manipulation
 *   document.getElementById(id).innerHTML = '<div class="loading"></div>';
 *   // ... more operations
 *   return true;
 * };
 *
 * const safeProcessClick = noParalellExecution(processClick);
 *
 * button.addEventListener('click', () => {
 *   const result = safeProcessClick('content-area');
 *   if (result === undefined) {
 *     console.log('Operation already in progress, click ignored');
 *   }
 * });
 *
 * // Asynchronous example - prevent API race conditions
 * const fetchUserData = async (userId: string) => {
 *   const response = await fetch(`/api/users/${userId}`);
 *   const data = await response.json();
 *   updateUserState(data); // Update global state
 *   return data;
 * };
 *
 * const safeFetchUserData = noParalellExecution(fetchUserData);
 *
 * // This can be called from multiple places, but only one will execute at a time
 * const result = await safeFetchUserData('user123');
 * if (result === undefined) {
 *   console.log('Another fetch already in progress');
 * }
 * ```
 *
 * @param func - The function to protect from parallel execution
 * @returns A wrapped function that prevents parallel execution
 */
export function noParalellExecution<T extends (...args: any[]) => any>(
  func: T
): (...args: Parameters<T>) => ReturnType<T> | undefined {
  let running = false; // Flag to track execution state

  return function wrapped(this: ThisParameterType<T>, ...args: Parameters<T>): ReturnType<T> | undefined {
    // Bail early if already running
    if (running) return undefined;

    running = true;
    try {
      const result = func.apply(this, args);

      // Handle Promise returns differently to ensure proper unblocking
      if (result && typeof (result as any).then === 'function') {
        // For async functions: unblock when Promise settles (resolve or reject)
        (result as Promise<unknown>).finally(() => {
          running = false;
        });
      } else {
        // For synchronous functions: unblock immediately after execution
        running = false;
      }
      return result as ReturnType<T>;
    } catch (err) {
      // On error, ensure we unblock before rethrowing
      running = false;
      throw err; // Preserve original error behavior
    }
  };
}

/**
 * Specialized version of noParalellExecution for async functions.
 * Ensures only one async execution can occur at a time, ignoring subsequent
 * calls until the current execution completes.
 *
 * Benefits over noParalellExecution:
 * - Simpler API for Promise-only functions
 * - Returns a Promise<void> for cleaner async/await usage
 * - Guaranteed to unblock even if the Promise chain breaks
 *
 * Example:
 * ```typescript
 * // Example: Prevent multiple simultaneous form submissions
 * const submitForm = async (formData: FormData) => {
 *   try {
 *     showLoadingIndicator();
 *     const response = await fetch('/api/submit', {
 *       method: 'POST',
 *       body: formData
 *     });
 *     const result = await response.json();
 *     showSuccess(result.message);
 *   } finally {
 *     hideLoadingIndicator();
 *   }
 * };
 *
 * // Create a protected version
 * const safeSubmitForm = noParalellExecutionAsync(submitForm);
 *
 * // Usage in event handler
 * submitButton.addEventListener('click', async () => {
 *   const formData = new FormData(formElement);
 *   await safeSubmitForm(formData);
 *   // If multiple clicks happen, only the first one executes
 *   // We don't need to check return values like with noParalellExecution
 * });
 *
 * // Can also be used with async/await
 * async function handleMultipleOperations() {
 *   await safeSubmitForm(formData1);
 *   await safeSubmitForm(formData2); // This waits for the first to complete
 * }
 * ```
 *
 * @param func - The async function to protect from parallel execution
 * @returns A wrapped async function that prevents parallel execution
 */
export function noParalellExecutionAsync<T extends (...args: any[]) => Promise<any>>(
  func: T
): (...args: Parameters<T>) => Promise<void> {
  let running = false;

  return async function wrapped(this: ThisParameterType<T>, ...args: Parameters<T>): Promise<void> {
    if (running) return; // Ignore calls while execution is in progress
    running = true;
    try {
      await func.apply(this, args);
    } finally {
      // Ensure we unblock in all cases (success, error, or unexpected behavior)
      running = false;
    }
  };
}

/**
 * Deduplicates concurrent calls to an async function by sharing a single Promise.
 *
 * Key behavior:
 * - First call initiates the actual execution
 * - Concurrent calls (before completion) receive the same Promise
 * - After completion, the next call will trigger a fresh execution
 *
 * This is especially useful for:
 * - API calls that might be triggered from multiple components
 * - Resource initialization that might happen concurrently
 * - Expensive computations that should not be duplicated
 *
 * Unlike noParalellExecution, this doesn't block or ignore calls - it
 * makes concurrent callers share the same execution and result.
 *
 * Example:
 * ```typescript
 * // Example 1: Data fetching with shared results
 * const fetchUserProfile = async (userId: string) => {
 *   console.log(`Actually fetching data for ${userId}`);
 *   const response = await fetch(`/api/users/${userId}`);
 *   const data = await response.json();
 *   return data;
 * };
 *
 * // Create a shared version
 * const sharedFetchUserProfile = sharedExecute(fetchUserProfile);
 *
 * // If these are called concurrently (e.g., from different components)
 * // only ONE network request will happen
 * const promise1 = sharedFetchUserProfile('user123');
 * const promise2 = sharedFetchUserProfile('user123');
 * const promise3 = sharedFetchUserProfile('user123');
 *
 * // All promises are the same object
 * console.log(promise1 === promise2); // true
 * console.log(promise2 === promise3); // true
 *
 * // All resolves get the same data
 * const [result1, result2, result3] = await Promise.all([promise1, promise2, promise3]);
 *
 * // Example 2: Resource initialization
 * const initializeDatabase = async () => {
 *   console.log('Starting expensive DB initialization');
 *   await loadSchema();
 *   await populateCache();
 *   return dbConnection;
 * };
 *
 * const sharedDbInit = sharedExecute(initializeDatabase);
 *
 * // Can be safely called from anywhere in the app
 * // Only one initialization will occur
 * export async function getDatabase() {
 *   return sharedDbInit();
 * }
 * ```
 *
 * @param event - The async function to deduplicate
 * @returns A function that deduplicates concurrent calls
 */
export function sharedExecute<TResult>(
  event: (...args: any[]) => Promise<TResult>
): (...args: any[]) => Promise<TResult> {
  // Tracks the currently in-flight Promise, if any
  let inflight: Promise<TResult> | undefined;

  return async (...args: any[]) => {
    // If there's no execution in progress, start one
    if (!inflight) {
      inflight = (async () => {
        try {
          // Execute the original function and return its result
          return await event(...args);
        } finally {
          // Clear the inflight flag to allow future executions
          // This happens whether the Promise resolves or rejects
          inflight = undefined;
        }
      })();
    }
    // Return the shared Promise for all concurrent callers
    return inflight;
  };
}
